import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAudio } from "@/context/useAudio";
import { logWarn } from "@/lib/log";

const PlayerContext = createContext(null);
const DEFAULT_ACCESS = { full: true, owner: false, preview_seconds: 30, preview_start_sec: 0 };

/**
 * Owns the playback queue + access state. All the noisy HTMLAudioElement
 * work lives in `useAudio`; this file is about queue order, track access,
 * and exposing a memoized context value.
 *
 * Play modes:
 *   - "preview" (default): unpurchased paid tracks play their 30-second hook
 *   - "radio":  playback runs the FULL song. Used by the home trending station,
 *               which is marketed as "Free radio. Always." — full-length free
 *               listening is the promise. Downloads still require purchase.
 */
export function PlayerProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [access, setAccess] = useState(DEFAULT_ACCESS);
  const [mode, setMode] = useState("preview");
  const current = queue[index] || null;

  // Radio playback bypasses the preview cap client-side. Downloads and
  // buy CTAs are still gated by real access, so nothing changes for revenue.
  const effectiveAccess = useMemo(
    () => (mode === "radio" ? { ...access, full: true } : access),
    [mode, access]
  );

  const advance = useCallback(
    () => setIndex((i) => (queue.length ? (i + 1) % queue.length : 0)),
    [queue.length]
  );

  const { progress, duration, load, toggle, seek, PREVIEW_SEC } = useAudio({
    current, playing, setPlaying, access: effectiveAccess, onEnded: advance,
  });

  useEffect(() => {
    if (!current) return;
    load(fileUrl(current.audio_path));
    if (!current.id) return;
    api.get(`/tracks/${current.id}/access`)
      .then((r) => setAccess(r.data))
      .catch((err) => {
        logWarn("[player] access fetch failed", err?.response?.status);
        setAccess({ full: false, owner: false, preview_seconds: PREVIEW_SEC, preview_start_sec: 0 });
      });
    api.post(`/tracks/${current.id}/play`).catch((err) => {
      logWarn("[player] play-count post failed", err?.response?.status);
    });
  }, [current, load, PREVIEW_SEC]);

  const startQueue = useCallback((track, list, newMode) => {
    setMode(newMode);
    if (list && list.length) {
      const idx = list.findIndex((t) => t.id === track.id);
      setQueue(list);
      setIndex(idx >= 0 ? idx : 0);
    } else {
      setQueue([track]);
      setIndex(0);
    }
    setPlaying(true);
  }, []);

  const playTrack = useCallback((track, list = null) => startQueue(track, list, "preview"), [startQueue]);
  const playRadio = useCallback((track, list = null) => startQueue(track, list, "radio"), [startQueue]);

  const prev = useCallback(
    () => setIndex((i) => (queue.length ? (i - 1 + queue.length) % queue.length : 0)),
    [queue.length]
  );

  const value = useMemo(
    () => ({
      current, queue, playing, progress, duration, access: effectiveAccess, mode,
      playTrack, playRadio, toggle, next: advance, prev, seek, PREVIEW_SEC,
    }),
    [current, queue, playing, progress, duration, effectiveAccess, mode, playTrack, playRadio, toggle, advance, prev, seek, PREVIEW_SEC]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export const usePlayer = () => useContext(PlayerContext);
